import _ from 'lodash'
import { RestApiClient } from 'ifield-core-components'
import ImageUtil from '@/js/util/ImageUtil.js'
import TexturePacker from '@/lib/TexturePacker.js'
import { getIconEmbeddedPin } from '@/js/CreateMapIcon.js'
import IconCluster from '@/assets/ifield/cluster-report@3x.png'
import IconClusterSelected from '@/assets/ifield/cluster-report-selected@3x.png'
import IconPin from '@/assets/ifield/report-pin@3x.png'
import IconPinBlank from '@/assets/ifield/report-pin-blank@3x.png'

// mapping
const MAPPING = {
  default: {
    x: 0,
    y: 0,
    width: 168,
    height: 192,
    anchorX: 84,
    anchorY: 168,
  },
  cluster: {
    x: 0,
    y: 0,
    width: 288,
    height: 192,
    anchorX: 144,
    anchorY: 96,
  },
  clusterSelected: {
    x: 0,
    y: 0,
    width: 288,
    height: 192,
    anchorX: 144,
    anchorY: 96,
  },
  pin: {
    x: 0,
    y: 0,
    width: 168,
    height: 192,
    anchorX: 84,
    anchorY: 168,
  },
}

/**
 * URLのイメージを取得し、Image URL 形式に変換
 * @param {String} url 画像URL
 * @return {Object} promise オブジェクト
 */
const fetchImage = (url) => {
  return new Promise((resolve, reject) => {
    RestApiClient.executeGetApiInResponse(url)
      .then((response) => response.blob())
      .then((blob) => {
        // Blob で取得して、Image URL 形式に変換する
        const reader = new FileReader()
        reader.onload = function () {
          resolve(this.result)
        }
        reader.readAsDataURL(blob)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

/**
 * 報告書プロジェクト画像取得
 * @param {String} id 報告書プロジェクトID
 * @returns {String} dataUrl
 */
const getReportProjectIcon = async (id) => {
  const url = RestApiClient.createApiUrl('download_file')
  const params = url.searchParams
  params.append('attach_file_type', 5)
  params.append('report_project_image', 1)
  id && params.append('id', id)
  return await fetchImage(url.toString())
}

/**
 * 報告書アイコン埋め込みピン画像を取得
 * @param {String} id 報告書プロジェクトID
 * @return {String} dataUrl
 */
const getPinImage = async (id) => {
  const reportProjectImage = await getReportProjectIcon(id)
  return await getIconEmbeddedPin(IconPinBlank, reportProjectImage)
}

const getImageMap = new Map()

/**
 * imageStoreとiconMappingが含むIDに差分が無い場合はtrue
 * (gettersだとMapの変更が検知されないので関数)
 * @param {Map} imageStore 画像ストア
 * @param {Object} mapping iconMapping
 * @returns {Boolean}
 */
const isImageIdsSame = (imageStore, mapping) => {
  const storeKeys = [...imageStore.keys()].map((id) => `${id}`)
  const mappingKeys = mapping ? [...Object.keys(mapping)] : []
  return _.difference(storeKeys, mappingKeys).length === 0
}

/**
 * 報告書マーカー保持Vuexモジュール
 */
export default {
  namespaced: true,
  state: {
    imageStore: new Map(),
    iconAtlas: null,
    iconMapping: null,
  },
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.imageStore.clear()
      state.iconAtlas = null
      state.iconMapping = null
    },
    /**
     * 画像をストア
     * @param {*} state
     * @param {String} id ユーザID
     * @param {String} image 画像データ
     */
    setImage(state, { id, image }) {
      if (image) {
        state.imageStore.set(id, image)
      }
    },
    /**
     * iconAtlas更新
     * @param {*} state
     * @param {ImageBitmap} atlas
     */
    setAtlas(state, atlas) {
      state.iconAtlas = atlas
    },
    /**
     * iconMapping更新
     * @param {*} state
     * @param {Object} mapping
     */
    setMapping(state, mapping) {
      state.iconMapping = mapping
    },
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * state初期化
     * @param {Boolean} force 強制的に初期化する場合はtrue
     * @returns {Promise.<Object>} 初期化後の{atlas, mapping}
     */
    async initializeState({ state, commit, dispatch }, force) {
      if (!force && state.iconAtlas && state.iconMapping) {
        // 既に存在しているのでそのまま返却
        return { atlas: state.iconAtlas, mapping: state.iconMapping }
      }

      // imageStoreにデフォルト使用マーカー登録
      const [pin, cluster, clusterSelected] = await Promise.all([
        ImageUtil.toImage(IconPin),
        ImageUtil.toImage(IconCluster),
        ImageUtil.toImage(IconClusterSelected),
      ])
      commit('setImage', { id: 'default', image: ImageUtil.toDataURL(pin) })
      commit('setImage', { id: 'cluster', image: ImageUtil.toDataURL(cluster) })
      commit('setImage', {
        id: 'clusterSelected',
        image: ImageUtil.toDataURL(clusterSelected),
      })

      // texture作成
      return await dispatch('updateTexture')
    },
    /**
     * texture state 更新
     * @param {Boolean} force 強制的に初期化する場合はtrue
     * @returns {Promise.<Object>} 更新後の{atlas, mapping}
     */
    async updateTexture({ commit, state }, force) {
      if (!force && isImageIdsSame(state.imageStore, state.iconMapping)) {
        return { atlas: state.iconAtlas, mapping: state.iconMapping }
      }

      const images = await Promise.all(
        [...state.imageStore.entries()].map(async ([id, image]) => ({
          id,
          atlas: await ImageUtil.toImage(image),
          mapping: MAPPING[id] || MAPPING.pin,
        }))
      )

      const packer = new TexturePacker()
      const { atlas, mapping } = await packer.pack(images)
      commit('setAtlas', atlas)
      commit('setMapping', mapping)

      return { atlas, mapping }
    },
    /**
     * 指定IDのピン画像取得
     * @param {Number} id ユーザーID
     * @param {Boolean} [force=false] 強制的に更新する場合はtrue
     */
    async getImageById({ state, commit }, { id, force = false }) {
      if (!force && state.imageStore.has(id)) {
        return state.imageStore.get(id)
      }
      let promise
      if (getImageMap.has(id)) {
        promise = getImageMap.get(id)
      } else {
        promise = getPinImage(id)
        getImageMap.set(id, promise)
      }
      const image = await promise
      commit('setImage', { id, image })
      getImageMap.delete(id)
      return image
    },
  },
}
